import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  StatsState,
  OverallStats,
  FlagStats,
  PracticeSession,
  MasteryLevel,
} from '@/types';

interface StatsActions {
  recordAnswer: (
    countryCode: string,
    correct: boolean,
    responseTime: number
  ) => void;
  recordSession: (session: Omit<PracticeSession, 'id'>) => void;
  getFlagStats: (countryCode: string) => FlagStats | undefined;
  getMasteryLevel: (countryCode: string) => MasteryLevel;
  getBestFlags: (count: number) => FlagStats[];
  getWorstFlags: (count: number) => FlagStats[];
  updateStreak: () => void;
  reset: () => void;
}

type StatsStore = StatsState & StatsActions;

const initialOverallStats: OverallStats = {
  totalQuizzes: 0,
  totalQuestions: 0,
  correctAnswers: 0,
  currentStreak: 0,
  longestStreak: 0,
  lastPracticeDate: 0,
  totalPracticeTime: 0,
};

const initialState: StatsState = {
  overall: initialOverallStats,
  byFlag: {},
  sessions: [],
};

function isSameDay(date1: number, date2: number): boolean {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function isConsecutiveDay(prev: number, current: number): boolean {
  const prevDate = new Date(prev);
  const currentDate = new Date(current);
  const diffTime = currentDate.getTime() - prevDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays === 1;
}

export const useStatsStore = create<StatsStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      recordAnswer: (countryCode, correct, responseTime) => {
        set((state) => {
          const existingStats = state.byFlag[countryCode];
          const newFlagStats: FlagStats = existingStats
            ? {
                ...existingStats,
                timesShown: existingStats.timesShown + 1,
                timesCorrect: existingStats.timesCorrect + (correct ? 1 : 0),
                averageResponseTime:
                  (existingStats.averageResponseTime *
                    existingStats.timesShown +
                    responseTime) /
                  (existingStats.timesShown + 1),
                lastSeen: Date.now(),
              }
            : {
                countryCode,
                timesShown: 1,
                timesCorrect: correct ? 1 : 0,
                averageResponseTime: responseTime,
                lastSeen: Date.now(),
              };

          return {
            overall: {
              ...state.overall,
              totalQuestions: state.overall.totalQuestions + 1,
              correctAnswers:
                state.overall.correctAnswers + (correct ? 1 : 0),
            },
            byFlag: {
              ...state.byFlag,
              [countryCode]: newFlagStats,
            },
          };
        });
      },

      recordSession: (session) => {
        const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        set((state) => ({
          overall: {
            ...state.overall,
            totalQuizzes: state.overall.totalQuizzes + 1,
            totalPracticeTime:
              state.overall.totalPracticeTime + session.duration,
            lastPracticeDate: Date.now(),
          },
          sessions: [{ ...session, id }, ...state.sessions].slice(0, 100), // Keep last 100 sessions
        }));
        get().updateStreak();
      },

      getFlagStats: (countryCode) => get().byFlag[countryCode],

      getMasteryLevel: (countryCode) => {
        const stats = get().byFlag[countryCode];
        if (!stats || stats.timesShown === 0) return 'new';

        const accuracy = stats.timesCorrect / stats.timesShown;

        // Mastered: 100% over 3+ attempts OR 80%+ over 5+ attempts
        if ((accuracy === 1 && stats.timesShown >= 3) || (accuracy >= 0.8 && stats.timesShown >= 5)) {
          return 'mastered';
        }
        // Familiar: >50% accuracy with 3+ attempts
        if (accuracy > 0.5 && stats.timesShown >= 3) return 'familiar';
        // Learning: everything else (seen but not got it down)
        return 'learning';
      },

      getBestFlags: (count) => {
        return Object.values(get().byFlag)
          .filter((f) => f.timesShown >= 5)
          .sort((a, b) => {
            const aAccuracy = a.timesCorrect / a.timesShown;
            const bAccuracy = b.timesCorrect / b.timesShown;
            return bAccuracy - aAccuracy;
          })
          .slice(0, count);
      },

      getWorstFlags: (count) => {
        return Object.values(get().byFlag)
          .filter((f) => f.timesShown >= 3)
          .sort((a, b) => {
            const aAccuracy = a.timesCorrect / a.timesShown;
            const bAccuracy = b.timesCorrect / b.timesShown;
            return aAccuracy - bAccuracy;
          })
          .slice(0, count);
      },

      updateStreak: () => {
        set((state) => {
          const now = Date.now();
          const lastPractice = state.overall.lastPracticeDate;

          if (lastPractice === 0) {
            return {
              overall: {
                ...state.overall,
                currentStreak: 1,
                longestStreak: 1,
              },
            };
          }

          if (isSameDay(lastPractice, now)) {
            return state;
          }

          if (isConsecutiveDay(lastPractice, now)) {
            const newStreak = state.overall.currentStreak + 1;
            return {
              overall: {
                ...state.overall,
                currentStreak: newStreak,
                longestStreak: Math.max(state.overall.longestStreak, newStreak),
              },
            };
          }

          // Streak broken
          return {
            overall: {
              ...state.overall,
              currentStreak: 1,
            },
          };
        });
      },

      reset: () => set(initialState),
    }),
    {
      name: 'flag-trainer-stats',
      version: 1,
    }
  )
);
